import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { Button, Card, Checkbox, Dropdown, Empty, Flex, Spin, Tag } from "antd"
import { MoreOutlined, PlusOutlined } from "@ant-design/icons"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { setFormTouched, setFormValues } from "store/proNewListingForms"
import { useAddOns } from "pagesV2/pro/Settings/AddOns/hooks/useAddOns"
import AddOnModal from "pagesV2/pro/Settings/AddOns/modal/AddOnModal"
import { ADD_ON_CONFIRM_COPY } from "pagesV2/pro/Settings/AddOns/constants"
import { NewConfirmModal } from "ui/NewConfirmModal"
import { Text } from "ui/Typography"
import AddOnPhoto from "ui/AddOnPhoto"
import { numberToCurrency } from "helpers/string"
import { spacings } from "themes/variables"
import styles from "./AddOns.module.css"

const AddOnRow = ({ addOn, checked, onChange, menuItems, isMutating }) => {
  const { t } = useTranslation()
  return (
    <div className={styles.row}>
      <Flex justify="space-between" align="center" gap={spacings.S}>
        <Checkbox checked={checked} onChange={(e) => onChange(addOn.id, e.target.checked)} disabled={!addOn.enabled}>
          <Flex align="center" gap={spacings.S}>
            <AddOnPhoto src={addOn.photo_url} alt={addOn.title} size={40} iconSize={18} />
            <Flex vertical gap={2}>
              <Flex align="center" gap={8}>
                <Text strong>{addOn.title}</Text>
                {!addOn.enabled && <Tag color="default">{t("settings.status_inactive")}</Tag>}
              </Flex>
              <Text className={styles.infoCategory}>{addOn.add_on_category?.name ?? ""}</Text>
            </Flex>
          </Flex>
        </Checkbox>
        <Flex align="center" gap={spacings.S}>
          {(addOn.min_per_order != null || addOn.max_per_order != null) && (
            <Text className={styles.rangeText}>
              {t("experiences.add_ons.range", {
                min: addOn.min_per_order ?? 0,
                max: addOn.max_per_order ?? "∞"
              })}
            </Text>
          )}
          <Text strong className={styles.price}>{numberToCurrency(Number(addOn.price ?? 0))}</Text>
          <Dropdown menu={{ items: menuItems }} trigger={["click"]} placement="bottomRight" disabled={isMutating}>
            <Button type="text" icon={<MoreOutlined />} />
          </Dropdown>
        </Flex>
      </Flex>
    </div>
  )
}

AddOnRow.propTypes = {
  addOn: PropTypes.object.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  menuItems: PropTypes.array.isRequired,
  isMutating: PropTypes.bool.isRequired
}

export const AddOns = ({ form, isFormInitialized }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const detailsFormValues = useSelector((state) => state.proNewListingForms?.details?.values)

  const {
    addOns,
    addOnsLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isMutating,
    isModalOpen,
    addOnToEdit,
    handleAddAddOn,
    openAddModal,
    closeModal,
    getMenuItems,
    confirmAction,
    closeConfirm,
    handleConfirm
  } = useAddOns({ enabled: true })

  const [selectedAddOnIds, setSelectedAddOnIds] = useState(() => form.getFieldValue("add_on_ids") || [])

  useEffect(() => {
    if (isFormInitialized) {
      setSelectedAddOnIds(form.getFieldValue("add_on_ids") || [])
    }
  }, [isFormInitialized, form])

  // Drop ids that became inactive or were deleted at the global level.
  useEffect(() => {
    if (addOns.length === 0) return
    const activeIds = new Set(addOns.filter((a) => a.enabled).map((a) => a.id))
    setSelectedAddOnIds((prev) => prev.filter((id) => activeIds.has(id)))
  }, [addOns])

  const handleToggle = (addOnId, checked) => {
    setSelectedAddOnIds((prev) => {
      const next = checked ? [...prev, addOnId] : prev.filter((id) => id !== addOnId)
      form.setFieldValue("add_on_ids", next)
      // Mirror into Redux details.values — that's what useTabSwitchSave reads from.
      dispatch(setFormValues("details", { ...(detailsFormValues || {}), add_on_ids: next }))
      dispatch(setFormTouched("details", true))
      return next
    })
  }

  const copy = confirmAction ? ADD_ON_CONFIRM_COPY[confirmAction.type] : null

  return (
    <>
      <Spin spinning={addOnsLoading}>
        <Card
          title={t("experiences.add_ons.section_title")}
          className="mb-3xl"
          extra={
            <Button color="primary" variant="solid" icon={<PlusOutlined />} onClick={openAddModal}>
              {t("experiences.add_ons.create_new")}
            </Button>
          }
        >
          <Text className={styles.description}>
            {t("experiences.add_ons.description")}
          </Text>
          {!addOnsLoading && addOns.length === 0 ? (
            <Empty description={t("experiences.add_ons.empty_title")} className={styles.empty} />
          ) : (
            <Flex vertical gap={spacings.XS} className="mt-m">
              {addOns.map((addOn) => (
                <AddOnRow
                  key={addOn.id}
                  addOn={addOn}
                  checked={selectedAddOnIds.includes(addOn.id)}
                  onChange={handleToggle}
                  menuItems={getMenuItems(addOn).filter(
                    (item) => item.key !== "manage-experiences" && !(item.key === "toggle" && addOn.enabled)
                  )}
                  isMutating={isMutating}
                />
              ))}
            </Flex>
          )}
        </Card>
      </Spin>

      <AddOnModal
        visible={isModalOpen}
        onCancel={closeModal}
        onSave={handleAddAddOn}
        initialValues={addOnToEdit}
        isSaving={isCreating || isUpdating}
      />
      <NewConfirmModal
        visible={!!confirmAction}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        title={copy ? t(copy.titleKey) : ""}
        message={copy ? t(copy.messageKey) : ""}
        highlightText={confirmAction?.addOn?.title}
        confirmLabel={copy ? t(copy.confirmKey) : ""}
        loading={isDeleting || isUpdating}
      />
    </>
  )
}

AddOns.propTypes = {
  form: PropTypes.object.isRequired,
  isFormInitialized: PropTypes.bool
}

AddOns.defaultProps = {
  isFormInitialized: false
}
